import { all, select, takeEvery, fork, put, call } from 'redux-saga/effects'

// action
import { shopActions, paymentActions } from '@redux/store/index'

// lib
import { Logger } from '@utils'
import config from '@configs'

// api
import { seasonProductApi, ResType } from '@api'

// schemas
import {
	SeasonProduct,
	SeasonProductType,
	ProductOption,
	ProductOptionType,
} from '@schemas'

/**
 * shop 상품 조회.
 *
 * seasonId 를 보내지 않는다 — 서버가 OPEN_YN=1 인 시즌을 판정한다.
 * 예전에는 .env 의 REACT_APP_SEASON_ID 를 실어 보냈고, 시즌을 바꾸려면
 * 재배포가 필요했다. (SEASON_OPEN_PLAN.md §10)
 */
const apiGetSeasonProduct = async (params: {
	accessToken: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await seasonProductApi.getShopSeasonProduct({})
	return res
}

const apiGetSeasonProductDetail = async (params: {
	sessionProductId: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await seasonProductApi.getShopSeasonProductDetail({
		params: { sessionProductId: params.sessionProductId },
	})
	return res
}

function* triggerInitShopPageSaga() {
	try {
		const seasonProductList: SeasonProductType[] = yield select(
			state => state.shop.productList,
		)
		const sessionAccessToken: string = sessionStorage.getItem('accessCustomerToken') ?? ''

		if (seasonProductList.length === 0) {
			yield put(shopActions.PRODUCT_LIST_STATUS('loading'))
		}

		const apiRes: ResType = yield call(apiGetSeasonProduct, {
			accessToken: sessionAccessToken,
		})

		if (apiRes.status === 200) {
			// 장바구니 무효화 기준이 되는 현재 시즌. 판매 중지 상태면 null 이 온다.
			yield put(shopActions.SEASON_ID(apiRes.data[0].season?.id ?? ''))

			const data = apiRes.data[0].productList as any[]
			const newSeaonsProductList: SeasonProductType[] = data.map(
				aData => new SeasonProduct(aData),
			)

			yield put(shopActions.PRODUCT_LIST(newSeaonsProductList))

			if (newSeaonsProductList.length > 0) {
				yield put(shopActions.PRODUCT_LIST_STATUS('ok'))
			} else {
				yield put(shopActions.PRODUCT_LIST_STATUS('empty'))
			}
		} else {
			yield put(shopActions.PRODUCT_LIST_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerInitShopItemPageSaga() {
	try {
		const seasonProductList: SeasonProductType[] = yield select(
			state => state.shop.productList,
		)
		const sessionAccessToken: string = sessionStorage.getItem('accessCustomerToken') ?? ''

		if (seasonProductList.length === 0) {
			yield put(shopActions.SHOP_ITEM_STATUS('loading'))
		}

		const apiRes: ResType = yield call(apiGetSeasonProduct, {
			accessToken: sessionAccessToken,
		})

		if (apiRes.status === 200) {
			yield put(shopActions.SEASON_ID(apiRes.data[0].season?.id ?? ''))

			const data = apiRes.data[0].productList as any[]
			const newSeaonsProductList: SeasonProductType[] = data.map(
				aData => new SeasonProduct(aData),
			)

			yield put(shopActions.PRODUCT_LIST(newSeaonsProductList))

			if (newSeaonsProductList.length > 0) {
				yield put(shopActions.SHOP_ITEM_STATUS('ok'))
			} else {
				yield put(shopActions.SHOP_ITEM_STATUS('empty'))
			}
		} else {
			yield put(shopActions.SHOP_ITEM_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* getShopItemOption() {
	try {
		const seasonProductList: SeasonProductType[] = yield select(
			state => state.shop.productList,
		)
		const productCode: string = yield select(state => state.shop.getShopItemOption)

		const filteredProduct = seasonProductList.filter(
			anItem => anItem.productCode === productCode.toUpperCase(),
		)

		if (filteredProduct.length === 0) return

		const sessionProductId = filteredProduct[0].productId
		const apiRes: ResType = yield call(apiGetSeasonProductDetail, {
			sessionProductId: sessionProductId,
		})

		if (apiRes.status === 200) {
			const data = apiRes.data as any[]
			const optionList: ProductOptionType[] = data.map(aData => new ProductOption(aData))

			yield put(shopActions.OPTION_LIST(optionList))
		} else {
			yield put(shopActions.OPTION_LIST([]))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(shopActions.TRIGGER_INIT_SHOP_PAGE, triggerInitShopPageSaga)
	yield takeEvery(shopActions.TRIGGER_INIT_SHOP_ITEM_PAGE, triggerInitShopItemPageSaga)
	yield takeEvery(shopActions.GET_SHOP_ITEM_OPTION, getShopItemOption)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
